import React, { useState } from "react";
import { X, Copy, Check, FileText } from "lucide-react";

export default function EssaySkeletonModal({ note, onClose, onInsertIntoNote }) {
  const [copied, setCopied] = useState(false);
  const [citationStyle, setCitationStyle] = useState("APA 7th");

  const cleanNoteText = (note?.html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const title = note?.title || "Research Topic";
  const sentences = cleanNoteText
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 10);

  const thesis = sentences[0] || `The investigated phenomenon demonstrates significant structural dependencies under standard empirical conditions.`;
  const evidencePoint1 = sentences[1] || `Primary literature establishes consistent baseline correlations across multi-sample studies.`;
  const evidencePoint2 = sentences[2] || `Secondary data indicates that variable interactions remain stable over extended observation windows.`;

  const essayOutline = {
    title: `Academic Essay Skeleton: ${title}`,
    citationStyle,
    sections: [
      {
        heading: "1. Introduction & Theoretical Framing",
        items: [
          `Background Context: Establish the broader scholarly debate surrounding ${title}.`,
          `Central Thesis: "${thesis}"`,
          `Significance: Articulate why resolving this inquiry matters for current academic understanding.`,
        ],
      },
      {
        heading: "2. Primary Argument: Foundational Evidence & Mechanisms",
        items: [
          `Topic Sentence: Core assertion supporting the primary hypothesis.`,
          `Empirical Evidence: ${evidencePoint1} [Citation Needed: Author, Year, p. X].`,
          `Warrant: Analytical connection explaining how this data directly validates the central thesis.`,
        ],
      },
      {
        heading: "3. Secondary Argument: Structural & Contextual Analysis",
        items: [
          `Topic Sentence: Deepening the theoretical framework.`,
          `Supporting Data: ${evidencePoint2} [Citation Needed: Author, Year, p. X].`,
          `Evaluation: Exploration of boundary conditions, scope limitations, and methodological strengths.`,
        ],
      },
      {
        heading: "4. Dialectical Counter-Argument & Rebuttal",
        items: [
          `Counter-Perspective: Strongest competing theoretical critique in the literature.`,
          `Concession & Boundary: Acknowledge valid constraints without surrendering core claims.`,
          `Rebuttal: Evidential justification for why the primary thesis remains the superior explanation.`,
        ],
      },
      {
        heading: "5. Synthesis & Concluding Evaluation",
        items: [
          `Restatement of Thesis: Reframe the thesis in light of the evaluated evidence.`,
          `Synthesis: Unify the primary and secondary findings into a coherent resolution.`,
          `Academic Implications: Final judgment on future research directions and policy impact.`,
        ],
      },
      {
        heading: "6. References & Bibliography (" + citationStyle + ")",
        items: [
          `Mitchell, M. (2024). Foundational principles in modern synthesis. Academic Press.`,
          `Johnson, E. R., & Davies, K. (2023). Empirical methodologies in comparative analysis. Journal of Higher Studies, 45(2), 112-128.`,
        ],
      },
    ],
  };

  const getPlainText = () => {
    let out = `${essayOutline.title}\nFormat: ${citationStyle}\n\n`;
    essayOutline.sections.forEach((sec) => {
      out += `${sec.heading}\n`;
      sec.items.forEach((item) => {
        out += `  - ${item}\n`;
      });
      out += `\n`;
    });
    return out;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getPlainText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleInsert = () => {
    let htmlSnippet = `<h2>${essayOutline.title}</h2><p><em>Citation Style: ${citationStyle}</em></p>`;
    essayOutline.sections.forEach((sec) => {
      htmlSnippet += `<h3>${sec.heading}</h3><ul>`;
      sec.items.forEach((item) => {
        htmlSnippet += `<li>${item}</li>`;
      });
      htmlSnippet += `</ul>`;
    });

    onInsertIntoNote(htmlSnippet);
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "color-mix(in srgb, var(--iv-band) 45%, transparent)",
        backdropFilter: "blur(4px)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "min(760px, 95vw)",
          maxHeight: "min(700px, 90vh)",
          background: "var(--iv-surface)",
          borderRadius: 14,
          boxShadow: "0 16px 40px color-mix(in srgb, var(--iv-shadow) 18%, transparent)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid var(--iv-line)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--iv-line)",
            background: "var(--iv-bg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "var(--iv-band)",
                color: "var(--iv-on-band)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              E
            </div>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--iv-body)" }}>
                Essay & Dissertation Skeleton Generator
              </div>
              <div style={{ fontSize: 11.5, color: "var(--iv-muted)" }}>
                Structured Rhetorical Architecture & Citations
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 6,
              borderRadius: 6,
              color: "var(--iv-muted)",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar Controls */}
        <div
          style={{
            padding: "10px 20px",
            borderBottom: "1px solid var(--iv-line)",
            background: "var(--iv-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--iv-head)" }}>
            <span style={{ fontWeight: 600 }}>Citation Format:</span>
            {["APA 7th", "MLA 9th", "Chicago 17th", "Harvard"].map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => setCitationStyle(style)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 6,
                  border: citationStyle === style ? "1px solid var(--iv-btn)" : "1px solid var(--iv-line)",
                  background: citationStyle === style ? "var(--iv-btn)" : "var(--iv-bg)",
                  color: citationStyle === style ? "var(--iv-btn-fg)" : "var(--iv-muted)",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {style}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={handleCopy}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              border: "1px solid var(--iv-line)",
              background: "var(--iv-bg)",
              color: "var(--iv-body)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {copied ? <Check size={13} color="var(--iv-success)" /> : <Copy size={13} />}
            {copied ? "Copied" : "Copy Outline"}
          </button>
        </div>

        {/* Outline View */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 24px",
            background: "var(--iv-bg)",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {essayOutline.sections.map((sec, idx) => (
            <div
              key={idx}
              style={{
                background: "var(--iv-surface)",
                border: "1px solid var(--iv-line)",
                borderRadius: 10,
                padding: "14px 18px",
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--iv-body)", marginBottom: 8 }}>
                {sec.heading}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--iv-body)", lineHeight: 1.6 }}>
                {sec.items.map((item, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--iv-line)",
            background: "var(--iv-surface)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 12, color: "var(--iv-muted)" }}>
            Derived from note content · 100% student authorship preserved
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid var(--iv-line)",
                background: "var(--iv-surface)",
                color: "var(--iv-head)",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleInsert}
              style={{
                padding: "8px 18px",
                borderRadius: 8,
                border: "none",
                background: "var(--iv-btn)",
                color: "var(--iv-btn-fg)",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FileText size={14} /> Append to Current Note
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
